import { baseTypeFromByte } from './baseTypes'
import { crc16 } from './crc'
import { FitDecodeError } from './errors'
import type {
  DataRecord,
  Definition,
  DefinitionRecord,
  DevFieldDef,
  FieldDef,
  FieldValue,
  FitFile,
  FitHeader,
  FitRecord,
} from './types'

const FIT_MAGIC = 0x2e464954 // ".FIT" as a big-endian uint32

/**
 * Decode a FIT file into an ordered record stream. Every record keeps its original byte
 * slice so an unedited re-encode is byte-identical; every field is additionally decoded so
 * the domain layer can read and edit it (see IMPLEMENTATION_PLAN.md "Codec").
 */
export function decode(bytes: Uint8Array): FitFile {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)

  if (bytes.length < 12) throw new FitDecodeError('file shorter than a FIT header')

  const headerSize = bytes[0]!
  if (headerSize !== 12 && headerSize !== 14) {
    throw new FitDecodeError(`unexpected header size ${headerSize}`)
  }
  const protocolVersion = bytes[1]!
  const profileVersion = view.getUint16(2, true)
  const dataSize = view.getUint32(4, true)
  if (view.getUint32(8, false) !== FIT_MAGIC) {
    throw new FitDecodeError('missing ".FIT" signature')
  }

  const dataStart = headerSize
  const dataEnd = headerSize + dataSize
  if (dataEnd + 2 > bytes.length) {
    throw new FitDecodeError('declared data size runs past end of file')
  }

  // Integrity: the trailing CRC covers header + data.
  const expectedCrc = view.getUint16(dataEnd, true)
  const actualCrc = crc16(bytes.subarray(0, dataEnd))
  if (expectedCrc !== actualCrc) {
    throw new FitDecodeError(`file CRC mismatch: expected ${expectedCrc}, computed ${actualCrc}`)
  }

  const header: FitHeader = {
    headerSize,
    protocolVersion,
    profileVersion,
    hasHeaderCrc: headerSize >= 14,
  }

  const records: FitRecord[] = []
  const definitions = new Map<number, Definition>()
  let pos = dataStart

  while (pos < dataEnd) {
    const recordStart = pos
    const recHeader = bytes[pos]!
    pos += 1

    if ((recHeader & 0x80) !== 0) {
      // Compressed-timestamp data record. Body follows its definition; pass through verbatim.
      const localType = (recHeader >> 5) & 0x3
      const timeOffset = recHeader & 0x1f
      const def = definitions.get(localType)
      if (!def) throw new FitDecodeError(`compressed record for undefined local type ${localType}`)
      const { values, devValues, end } = readDataBody(bytes, view, pos, def)
      records.push({
        kind: 'data',
        def,
        values,
        devValues,
        compressed: true,
        timeOffset,
        raw: bytes.slice(recordStart, end),
        dirty: false,
      })
      pos = end
      continue
    }

    const isDefinition = (recHeader & 0x40) !== 0
    const hasDevData = (recHeader & 0x20) !== 0
    const localType = recHeader & 0x0f

    if (isDefinition) {
      const def = readDefinition(bytes, view, pos, localType, hasDevData)
      definitions.set(localType, def.def)
      records.push({
        kind: 'definition',
        def: def.def,
        raw: bytes.slice(recordStart, def.end),
        dirty: false,
      })
      pos = def.end
    } else {
      const def = definitions.get(localType)
      if (!def) throw new FitDecodeError(`data record for undefined local type ${localType}`)
      const { values, devValues, end } = readDataBody(bytes, view, pos, def)
      records.push({
        kind: 'data',
        def,
        values,
        devValues,
        compressed: false,
        timeOffset: 0,
        raw: bytes.slice(recordStart, end),
        dirty: false,
      })
      pos = end
    }
  }

  return { header, records }
}

function readDefinition(
  bytes: Uint8Array,
  view: DataView,
  pos: number,
  localType: number,
  hasDevData: boolean,
): { def: DefinitionRecord['def']; end: number } {
  const reserved = bytes[pos]!
  const architecture = bytes[pos + 1]!
  const littleEndian = architecture === 0
  const globalMessageNumber = view.getUint16(pos + 2, littleEndian)
  const numFields = bytes[pos + 4]!
  let p = pos + 5

  const fields: FieldDef[] = []
  for (let i = 0; i < numFields; i++) {
    fields.push({ fieldDefNum: bytes[p]!, size: bytes[p + 1]!, baseTypeByte: bytes[p + 2]! })
    p += 3
  }

  const devFields: DevFieldDef[] = []
  if (hasDevData) {
    const numDev = bytes[p]!
    p += 1
    for (let i = 0; i < numDev; i++) {
      devFields.push({ fieldNum: bytes[p]!, size: bytes[p + 1]!, devDataIndex: bytes[p + 2]! })
      p += 3
    }
  }

  return {
    def: {
      localType,
      architecture,
      globalMessageNumber,
      reserved,
      hasDevData,
      fields,
      devFields,
    },
    end: p,
  }
}

function readDataBody(
  bytes: Uint8Array,
  view: DataView,
  pos: number,
  def: Definition,
): { values: FieldValue[]; devValues: Uint8Array[]; end: number } {
  const littleEndian = def.architecture === 0
  const values: FieldValue[] = []
  let p = pos

  for (const field of def.fields) {
    values.push(readField(bytes, view, p, field, littleEndian))
    p += field.size
  }

  const devValues: Uint8Array[] = []
  for (const dev of def.devFields) {
    devValues.push(bytes.slice(p, p + dev.size))
    p += dev.size
  }

  return { values, devValues, end: p }
}

function readField(
  bytes: Uint8Array,
  view: DataView,
  offset: number,
  field: FieldDef,
  littleEndian: boolean,
): FieldValue {
  const info = baseTypeFromByte(field.baseTypeByte)
  // Non-integer types, or a size that is not a clean multiple of the element size, are kept
  // opaque so they round-trip byte-for-byte.
  if (!info.integer || field.size % info.size !== 0) {
    return bytes.slice(offset, offset + field.size)
  }

  const count = field.size / info.size
  const read = (i: number): number =>
    readInt(view, offset + i * info.size, info.size, info.signed, littleEndian)
  if (count === 1) return read(0)
  const arr: number[] = new Array(count)
  for (let i = 0; i < count; i++) arr[i] = read(i)
  return arr
}

function readInt(
  view: DataView,
  offset: number,
  size: number,
  signed: boolean,
  littleEndian: boolean,
): number {
  switch (size) {
    case 1:
      return signed ? view.getInt8(offset) : view.getUint8(offset)
    case 2:
      return signed ? view.getInt16(offset, littleEndian) : view.getUint16(offset, littleEndian)
    case 4:
      return signed ? view.getInt32(offset, littleEndian) : view.getUint32(offset, littleEndian)
    default:
      // Unreachable: integer base types are 1, 2 or 4 bytes.
      throw new FitDecodeError(`unsupported integer size ${size}`)
  }
}
